import { useState, useMemo } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { X, Copy, Loader2 } from 'lucide-react';
import { useApi } from '../../lib/api.js';

// =============================================================================
// DuplicateBookingModal — Slice 7d.duplicate
// =============================================================================
// Multi-select calendar — dispatcher picks 1+ dates, optionally toggles which
// trip-specific fields to carry over, submits one batch → backend creates N
// independent bookings (each own master_ref, payment_method='account',
// allocation reset).
// =============================================================================

const MAX_DATES = 90;

function isoDate(d) {
    // Format YYYY-MM-DD from local date components. Avoids the toISOString
    // UTC bug — local-midnight 6 May during BST becomes UTC 5 May 23:00,
    // which sliced gives "2026-05-05" (off by one). Building the string
    // from getFullYear/getMonth/getDate uses the local zone consistently.
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
}

function startOfMonth(d) {
    return new Date(d.getFullYear(), d.getMonth(), 1);
}

function addMonths(d, n) {
    return new Date(d.getFullYear(), d.getMonth() + n, 1);
}

function daysInMonth(year, month) {
    return new Date(year, month + 1, 0).getDate();
}

function formatMonthLabel(d) {
    return d.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
}

function formatDateChip(iso) {
    const [y, m, day] = iso.split('-').map(Number);
    const d = new Date(y, m - 1, day);
    return d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
}

export default function DuplicateBookingModal({ booking, onClose }) {
    const navigate = useNavigate();
    const api = useApi();
    const qc = useQueryClient();

    // The booking's pickup_date may come back as a full ISO timestamp
    // (e.g. "2026-05-06T00:00:00.000Z") or a bare date — normalise to YYYY-MM-DD.
    const sourcePickupIso = String(booking.pickup_date || '').slice(0, 10);

    // Default: pickup_date + 1 day. Calendar opens on that month.
    const defaultStart = useMemo(() => {
        const [y, m, d] = sourcePickupIso.split('-').map(Number);
        if (!y || !m || !d) return new Date();
        const base = new Date(y, m - 1, d);
        base.setDate(base.getDate() + 1);
        return base;
    }, [sourcePickupIso]);

    const [calendarMonth, setCalendarMonth] = useState(startOfMonth(defaultStart));
    const [selectedDates, setSelectedDates] = useState(() => new Set());

    const [copyFlightDetails, setCopyFlightDetails] = useState(false);
    const [copyInternalNotes, setCopyInternalNotes] = useState(false);
        const [copyPaxComments, setCopyPaxComments] = useState(false);
    const [copyChauffeurNotes, setCopyChauffeurNotes] = useState(false);

    const [error, setError] = useState(null);

    const hasFlightDetails = !!(booking.flight_number || booking.tail_number || booking.fbo_code);
    const hasInternalNotes = !!(booking.internal_notes && booking.internal_notes.trim());
        const hasPaxComments = !!(booking.pax_comments && booking.pax_comments.trim());
    const hasChauffeurNotes = !!(booking.chauffeur_notes && booking.chauffeur_notes.trim());

    const toggleDate = (iso) => {
        setSelectedDates((prev) => {
            const next = new Set(prev);
            if (next.has(iso)) {
                next.delete(iso);
            } else {
                if (next.size >= MAX_DATES) {
                    setError(`Maximum ${MAX_DATES} dates per duplicate batch.`);
                    return prev;
                }
                next.add(iso);
                setError(null);
            }
            return next;
        });
    };

    const clearAll = () => setSelectedDates(new Set());

    const mutation = useMutation({
        mutationFn: async () => {
            const dates = Array.from(selectedDates).sort();
            return api.post(`/bookings/${booking.id}/duplicate`, {
                target_dates: dates,
                reverse: false,
                copy_flight_details: copyFlightDetails,
                copy_internal_notes: copyInternalNotes,
                                copy_pax_comments: copyPaxComments,
                copy_chauffeur_notes: copyChauffeurNotes,
            });
        },
        onSuccess: (data) => {
            qc.invalidateQueries({ queryKey: ['bookings'] });
            qc.invalidateQueries({ queryKey: ['bookings', 'list'] });
         if (data.bookings.length === 1) {
                onClose();
                // replace:true so the browser back button goes to wherever
                // the dispatcher was (typically /bookings list), not back
                // to the source booking they were on when they clicked
                // Duplicate. Source booking still reachable via the
                // sibling/ref linkage on the new booking's detail page.
                navigate(`/bookings/${data.bookings[0].id}`, { replace: true });
            } else {
                onClose();
            }
        },
        onError: (err) => {
            const msg = err?.response?.data?.error || err?.message || 'Failed to duplicate booking';
            setError(msg);
        },
    });

    const calendarCells = useMemo(() => {
        const year = calendarMonth.getFullYear();
        const month = calendarMonth.getMonth();
        const firstDay = new Date(year, month, 1);
        const totalDays = daysInMonth(year, month);
        const todayMidnight = new Date(new Date().toDateString());

        const firstDayWeekday = (firstDay.getDay() + 6) % 7;

        const cells = [];
        for (let i = 0; i < firstDayWeekday; i++) cells.push(null);
        for (let day = 1; day <= totalDays; day++) {
            const d = new Date(year, month, day);
            cells.push({
                day,
                iso: isoDate(d),
                isPast: d < todayMidnight,
                isSource: isoDate(d) === sourcePickupIso,
            });
        }
        while (cells.length < 42) cells.push(null);
        return cells;
    }, [calendarMonth, sourcePickupIso]);

    const sortedSelected = Array.from(selectedDates).sort();

    return (
        <div
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-y-auto"
            onClick={(e) => {
                if (e.target === e.currentTarget && !mutation.isPending) onClose();
            }}
        >
            <div className="bg-white rounded-lg shadow-2xl w-full max-w-2xl my-8">
                <div className="flex items-center justify-between p-5 border-b border-slate-200">
                    <div className="flex items-center gap-2.5">
                        <Copy className="w-5 h-5 text-emerald-600" />
                        <h2 className="text-lg font-semibold text-ink">Duplicate booking</h2>
                    </div>
                    <button
                        onClick={() => onClose()}
                        disabled={mutation.isPending}
                        className="p-1.5 hover:bg-slate-100 rounded transition disabled:opacity-50"
                    >
                        <X className="w-4 h-4" />
                    </button>
                </div>

                <div className="p-5 space-y-5">
                    <div className="rounded-md bg-slate-50 border border-slate-200 px-3.5 py-2.5 text-sm">
                        <div className="text-xs uppercase tracking-wide text-ink-subtle font-medium mb-0.5">
                            Copy from
                        </div>
                        <div className="font-mono text-sm text-ink">{booking.ride_ref}</div>
                        <div className="text-xs text-ink-subtle mt-0.5">
                            {sourcePickupIso} · {booking.pickup_time?.slice(0, 5)} ·{' '}
                            {booking.mode === 'hourly' ? `${booking.hours}h hourly` : 'one-way'}
                        </div>
                    </div>

                    <div>
                        <div className="flex items-center justify-between mb-2">
                            <label className="text-xs uppercase tracking-wide text-ink-subtle font-medium">
                                Pick dates ({selectedDates.size} selected)
                            </label>
                            {selectedDates.size > 0 && (
                                <button
                                    type="button"
                                    onClick={clearAll}
                                    className="text-xs text-ink-subtle hover:text-ink underline"
                                >
                                    Clear all
                                </button>
                            )}
                        </div>

                        <div className="border border-slate-200 rounded-md overflow-hidden">
                            <div className="flex items-center justify-between px-3 py-2 bg-slate-50 border-b border-slate-200">
                                <button
                                    type="button"
                                    onClick={() => setCalendarMonth((m) => addMonths(m, -1))}
                                    className="px-2 py-1 hover:bg-slate-200 rounded text-sm"
                                >‹</button>
                                <div className="text-sm font-medium text-ink">
                                    {formatMonthLabel(calendarMonth)}
                                </div>
                                <button
                                    type="button"
                                    onClick={() => setCalendarMonth((m) => addMonths(m, 1))}
                                    className="px-2 py-1 hover:bg-slate-200 rounded text-sm"
                                >›</button>
                            </div>

                            <div className="grid grid-cols-7 text-[10px] uppercase tracking-wide text-ink-subtle font-medium border-b border-slate-200">
                                {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((d) => (
                                    <div key={d} className="px-1.5 py-1.5 text-center">{d}</div>
                                ))}
                            </div>

                            <div className="grid grid-cols-7">
                                {calendarCells.map((cell, i) => {
                                    if (!cell) {
                                        return <div key={i} className="h-10 bg-slate-50/50" />;
                                    }
                                    const selected = selectedDates.has(cell.iso);
                                    let cls = 'text-ink hover:bg-slate-100 cursor-pointer';
                                    if (selected) cls = 'bg-emerald-600 text-white font-medium hover:bg-emerald-700';
                                    else if (cell.isPast) cls = 'text-slate-300 cursor-not-allowed';
                                    return (
                                        <button
                                            key={i}
                                            type="button"
                                            onClick={() => !cell.isPast && toggleDate(cell.iso)}
                                            disabled={cell.isPast}
                                            className={`h-10 flex items-center justify-center text-sm transition relative ${cls}`}
                                        >
                                            {cell.day}
                                            {cell.isSource && (
                                                <span className="absolute bottom-1 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-amber-500" />
                                            )}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        {sortedSelected.length > 0 && (
                            <div className="mt-2.5 flex flex-wrap gap-1.5">
                                {sortedSelected.map((iso) => (
                                    <button
                                        key={iso}
                                        type="button"
                                        onClick={() => toggleDate(iso)}
                                        className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-emerald-50 text-emerald-700 border border-emerald-200 rounded hover:bg-emerald-100"
                                    >
                                        {formatDateChip(iso)}
                                        <X className="w-3 h-3" />
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                                        {(hasFlightDetails || hasInternalNotes || hasPaxComments || hasChauffeurNotes) && (
                        <div>
                            <label className="text-xs uppercase tracking-wide text-ink-subtle font-medium mb-2 block">
                                Carry over (optional)
                            </label>
                            <div className="space-y-2">
                                {hasFlightDetails && (
                                    <label className="flex items-start gap-2.5 cursor-pointer">
                                        <input type="checkbox" checked={copyFlightDetails} onChange={(e) => setCopyFlightDetails(e.target.checked)} className="mt-0.5" />
                                        <div className="text-sm">
                                            <div className="text-ink">Flight details</div>
                                            <div className="text-xs text-ink-subtle">Flight number, tail number, FBO</div>
                                        </div>
                                    </label>
                                )}
                                {hasInternalNotes && (
                                    <label className="flex items-start gap-2.5 cursor-pointer">
                                        <input type="checkbox" checked={copyInternalNotes} onChange={(e) => setCopyInternalNotes(e.target.checked)} className="mt-0.5" />
                                        <div className="text-sm">
                                            <div className="text-ink">Internal notes</div>
                                            <div className="text-xs text-ink-subtle">Dispatcher-only notes from the source booking</div>
                                        </div>
                                    </label>
                                )}
                                {hasPaxComments && (
                                    <label className="flex items-start gap-2.5 cursor-pointer">
                                        <input type="checkbox" checked={copyPaxComments} onChange={(e) => setCopyPaxComments(e.target.checked)} className="mt-0.5" />
                                        <div className="text-sm">
                                                                                        <div className="text-ink">Booking notes</div>
                                            <div className="text-xs text-ink-subtle">Visible to customer and chauffeur</div>
                                        </div>
                                    </label>
                                )}
                                {hasChauffeurNotes && (
                                    <label className="flex items-start gap-2.5 cursor-pointer">
                                        <input type="checkbox" checked={copyChauffeurNotes} onChange={(e) => setCopyChauffeurNotes(e.target.checked)} className="mt-0.5" />
                                        <div className="text-sm">
                                            <div className="text-ink">Chauffeur notes</div>
                                            <div className="text-xs text-ink-subtle">Chauffeur-only notes (not shown to customer)</div>
                                        </div>
                                    </label>
                                )}
                            </div>
                        </div>
                    )}

                    <div className="rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-900">
                        <div className="font-medium mb-0.5">What carries over</div>
                        Customer, booker, passenger, addresses, times, vehicle class, quoted price.
                        <div className="font-medium mt-1.5 mb-0.5">What resets</div>
                        Allocation, payment method (→ Account), Stripe state, email timestamps.
                    </div>

                    {error && (
                        <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">
                            {error}
                        </div>
                    )}
                </div>

                <div className="flex items-center justify-between p-5 border-t border-slate-200 bg-slate-50 rounded-b-lg">
                    <div className="text-sm text-ink-subtle">
                        {selectedDates.size === 0
                            ? 'Pick at least one date'
                            : selectedDates.size === 1
                                ? '1 booking will be created'
                                : `${selectedDates.size} bookings will be created`}
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            type="button"
                            onClick={() => onClose()}
                            disabled={mutation.isPending}
                            className="px-3.5 py-2 text-sm text-ink-subtle hover:text-ink transition disabled:opacity-50"
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            onClick={() => mutation.mutate()}
                            disabled={selectedDates.size === 0 || mutation.isPending}
                            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-300 disabled:cursor-not-allowed rounded transition"
                        >
                            {mutation.isPending ? (
                                <>
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                    Creating…
                                </>
                            ) : (
                                <>
                                    <Copy className="w-4 h-4" />
                                    Duplicate
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
